import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Truck, X } from "lucide-react";
import { api } from "../lib/api";

const BAND = "#6B7280";
const covers = (r, iso) => r.date_from <= iso && iso <= (r.date_to || r.date_from);
const fmtShort = (iso) => { const [, m, d] = iso.split("-"); return `${+m}/${+d}`; };

// Picker that opens where the drag ended; a clash asks for an override reason (logged by the server).
function ReservePicker({ pick, projects, onClose, onChanged }) {
  const { vehicle, from, to, x, y } = pick;
  const reserve = async (pid) => {
    const body = { vehicle_id: vehicle.id, project_id: pid, date_from: from, date_to: to };
    try { await api.post("/dispatch/vehicle-reservations", body); toast.success(`${vehicle.name} reserved`); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status !== 409 || d?.warning !== "vehicle_conflict") { toast.error(typeof d === "string" ? d : "Reservation failed"); return; }
      const why = window.prompt(`${d.detail}. Override — reason?`); if (!why) return;
      await api.post("/dispatch/vehicle-reservations", { ...body, override_reason: why }); toast.success("Reserved (override logged)");
    }
    onClose(); onChanged();
  };
  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed z-50 w-64 rounded-md border border-border bg-popover p-2 shadow-lg"
           style={{ left: Math.min(x, window.innerWidth - 272), top: Math.min(y + 8, window.innerHeight - 90) }}
           data-testid="vehicle-reserve-pop">
        <div className="mb-1 text-[11px] font-semibold text-muted-foreground" data-testid="vehicle-reserve-range">
          Reserve {vehicle.name} · {from === to ? fmtShort(from) : `${fmtShort(from)} – ${fmtShort(to)}`}
        </div>
        <select autoFocus defaultValue="" onChange={(e) => e.target.value && reserve(e.target.value)}
                className="h-8 w-full rounded border border-border bg-background px-2 text-xs" data-testid="vehicle-reserve-job">
          <option value="">Pick a job…</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.job_number ? `${p.job_number} · ` : ""}{p.project_name}</option>)}
        </select>
      </div>
    </>, document.body
  );
}

/** Warehouse › Vehicle Crew: a band under Warehouse/Delivery, one row per vehicle. Press + drag empty days to book a stretch. */
export default function VehicleCrewRows({ vehicles, reservations, dates, fmtIso, isWeekend, projects, onChanged }) {
  const [drag, setDrag] = useState(null);   // { vehicle, start, end }
  const [pick, setPick] = useState(null);
  useEffect(() => {
    if (!drag) return undefined;
    const up = (e) => {
      const [from, to] = [drag.start, drag.end].sort();
      setPick({ vehicle: drag.vehicle, from, to, x: e.clientX, y: e.clientY }); setDrag(null);
    };
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, [drag]);
  if (!vehicles.length) return null;
  const inDrag = (vid, iso) => drag && drag.vehicle.id === vid && iso >= [drag.start, drag.end].sort()[0] && iso <= [drag.start, drag.end].sort()[1];
  const cancel = async (r) => {
    if (!window.confirm(`Cancel the ${r.date_from}${r.date_to && r.date_to !== r.date_from ? ` – ${r.date_to}` : ""} reservation for ${r.project_name}?`)) return;
    try { await api.delete(`/dispatch/vehicle-reservations/${r.id}`); toast.success("Reservation cancelled"); onChanged(); }
    catch { toast.error("Cancel failed"); }
  };
  return (
    <>
      <tr data-testid="sched-vehicle-band">
        <td colSpan={dates.length + 1} className="border-b border-gray-200 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white" style={{ background: BAND }}>
          Vehicle Crew · {vehicles.length}
        </td>
      </tr>
      {vehicles.map((v) => (
        <tr key={v.id} data-testid={`sched-vehicle-row-${v.id}`} className="select-none">
          <td className="border-b border-r border-gray-200 px-2 py-1.5 bg-white" style={{ position: "sticky", left: 0, zIndex: 5 }}>
            <div className="flex items-center gap-1.5">
              <Truck className="h-3 w-3 text-gray-500" />
              <div className="leading-tight">
                <div className="text-[12px] font-medium text-gray-900 truncate">{v.name}</div>
                <div className="text-[10px] text-gray-500">{v.type || "Vehicle"}</div>
              </div>
            </div>
          </td>
          {dates.map((d) => {
            const iso = fmtIso(d);
            const r = reservations.find((x) => x.vehicle_id === v.id && covers(x, iso));
            const sel = inDrag(v.id, iso);
            return (
              <td key={iso} className={`border-b border-r border-gray-200 p-0 text-[11px] ${sel ? "bg-blue-200" : ""}`}
                  style={!r && !sel && isWeekend(d) ? { background: "var(--cell-holiday-bg, #E5E7EB)" } : undefined}
                  onMouseEnter={() => drag && drag.vehicle.id === v.id && !r && setDrag((g) => ({ ...g, end: iso }))}>
                {r ? (
                  <div className={`group flex items-center gap-1 px-1.5 py-1 ${r.conflicts?.length ? "bg-red-50 text-red-700 font-semibold" : "bg-slate-100 text-gray-800"}`}
                       title={`${r.project_name} · ${r.date_from}${r.date_to && r.date_to !== r.date_from ? ` – ${r.date_to}` : ""}${r.conflicts?.length ? " · CONFLICT" : ""}`}
                       data-testid={`vehicle-reservation-${r.id}-${iso}`}>
                    <span className="truncate">{r.project_name}</span>
                    <button type="button" onClick={() => cancel(r)} className="ml-auto hidden text-gray-500 hover:text-gray-900 group-hover:block"
                            title="Cancel this reservation" data-testid={`vehicle-reservation-cancel-${r.id}-${iso}`}><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className={`h-full w-full min-h-[28px] cursor-pointer ${sel ? "" : "hover:bg-blue-50"}`}
                       title={`Reserve ${v.name} — click a day or drag across days`}
                       data-testid={`vehicle-cell-${v.id}-${iso}`}
                       onMouseDown={(e) => { if (e.button === 0) { e.preventDefault(); setDrag({ vehicle: v, start: iso, end: iso }); } }} />
                )}
              </td>
            );
          })}
        </tr>
      ))}
      {pick && <ReservePicker pick={pick} projects={projects} onClose={() => setPick(null)} onChanged={onChanged} />}
    </>
  );
}
